import { Card, CardContent, CardHeader, Combobox, Input, Password, ScrollArea, Switch } from '@ibsys/kit';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useRoles } from '@/entities/roles';
import { PasswordRulesHint, User, useEnglishLayoutInput } from '@/entities/users';
import { cn, getFullName, SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { SaveButton } from '@/shared/ui';
import { UserFormValues } from './types';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { useUserForm } from './useUserForm';
import { ServiceTokensList } from '../ServiceTokensList';
import { UserGroupsList } from '../UserGroupsList';
import { UserImpersonateButton } from './UserImpersonateButton';
import { UserResetPassword } from './UserResetPassword';

type Props = {
  initial?: User;
  onSuccess?: () => void;
  onDelete?: () => void;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
};

type UserFormValuesLoose = Omit<Partial<UserFormValues>, 'userGroups'> & {
  userGroups?: Array<{ id?: number }>;
};

const normalizeUserFormValues = (values: UserFormValuesLoose) => ({
  firstName: values.firstName ?? '',
  lastName: values.lastName ?? '',
  middleName: values.middleName ?? '',
  email: values.email ?? '',
  roleCode: values.roleCode ?? '',
  isActive: values.isActive ?? true,
  userGroups: (values.userGroups ?? [])
    .map((group) => group.id)
    .filter((id): id is number => typeof id === 'number')
    .sort((a, b) => a - b),
  password: values.password ?? '',
});

export const UserForm: FC<Props> = ({ initial, onSuccess, onDelete, onUnsavedChange }) => {
  const { t } = useTranslation(['common', 'users', 'roles']);

  const { form, isEdit, isLoading, isDisabled, onSubmit, defaultValues } = useUserForm({
    initial,
    onSuccess,
  });
  // Email и ФИО заводимого пользователя входят в правила его пароля: их правка перепроверяет пароль
  const personalDataRules = isEdit ? undefined : { deps: 'password' as const };
  const englishLayoutInput = useEnglishLayoutInput();

  const { control, watch } = form;
  const watchedValues = useWatch({ control });

  const { data: { roles = [] } = {} } = useRoles({});

  const rolesOptions = useMemo(
    () =>
      roles
        .map((role) => ({
          label: role.name,
          value: role.code,
          disabled: !!role.isDeleted,
          description: role.isDeleted ? t('roles:isNotActive') : role.isSystem ? t('roles:systemRole') : '',
        }))
        .sort((a) => (!a.disabled ? -1 : 0)) || [],
    [roles, t],
  );

  const selectedRole = roles.find((role) => role.code === watch('roleCode'));
  const roleError = selectedRole?.isDeleted ? t('users:roleDeletedError') : '';

  const titlePlaceholder = isEdit ? t('users:editingUser') : t('users:creatingUser');

  const fullName = initial ? getFullName(initial.firstName, initial.lastName, initial.middleName) : '';
  const hasUnsavedChanges = useMemo(() => {
    if (!watchedValues) {
      return false;
    }
    const normalizedValues = normalizeUserFormValues(watchedValues);
    const normalizedDefaults = normalizeUserFormValues(defaultValues);
    return JSON.stringify(normalizedValues) !== JSON.stringify(normalizedDefaults);
  }, [defaultValues, watchedValues]);

  useEffect(() => {
    onUnsavedChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChange]);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b flex justify-between items-center">
        <span className="text-xl font-semibold">{fullName || titlePlaceholder}</span>
        {!isDisabled && (
          <SaveButton
            isDirty={hasUnsavedChanges}
            isLoading={isLoading}
            onClick={onSubmit}
            secondaryAction={
              initial &&
              onDelete && (
                <ConfirmationButton
                  label={t('users:deleteUser')}
                  onConfirm={onDelete}
                  title={t('users:removeUserTitle')}
                  description={<Trans>{t('users:removeUserDescription', { name: fullName })}</Trans>}
                />
              )
            }
          />
        )}
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        <FormProvider {...form}>
          <ScrollArea className={cn('h-full', SCROLL_AREA_VIEWPORT_BLOCK)}>
            <div className="flex flex-col gap-4 p-6 max-w-2xl">
              <div className="grid grid-cols-2 gap-4 items-start">
                <Switch
                  label={t('users:activeUser')}
                  control={control}
                  name="isActive"
                  disabled={isDisabled}
                  className={'col-span-2'}
                />
                <Input
                  control={control}
                  name="lastName"
                  label={t('users:lastName')}
                  required
                  disabled={isDisabled}
                  rules={personalDataRules}
                />
                <Input
                  control={control}
                  name="firstName"
                  label={t('users:firstName')}
                  required
                  disabled={isDisabled}
                  rules={personalDataRules}
                />
                <Input
                  control={control}
                  name="middleName"
                  label={t('users:middleName')}
                  disabled={isDisabled}
                  rules={personalDataRules}
                />
                <Input
                  control={control}
                  name="email"
                  label={t('users:email')}
                  required
                  disabled={isDisabled}
                  rules={personalDataRules}
                />
                <Combobox
                  label={t('users:role')}
                  options={rolesOptions}
                  control={control}
                  name="roleCode"
                  required
                  disabled={isDisabled}
                  error={roleError}
                />
              </div>

              <UserGroupsList disabled={isDisabled} />

              {/* Список токенов доступен только по праву администрирования пользователей */}
              {initial && !isDisabled && <ServiceTokensList userId={initial.id} />}

              {!isEdit && !isDisabled && (
                <Password
                  {...englishLayoutInput}
                  className="max-w-100"
                  label={
                    <>
                      {t('users:password')}
                      <PasswordRulesHint />
                    </>
                  }
                  control={control}
                  name="password"
                  required
                  autoComplete={'new-password'}
                />
              )}

              {initial && !isDisabled && <UserResetPassword className="mt-5 max-w-100" user={initial} />}

              {/* Право на работу от чужого имени не связано с администрированием пользователей,
                  поэтому кнопка не смотрит на isDisabled — только на открытую карточку */}
              {initial && <UserImpersonateButton className="mt-5" user={initial} />}
            </div>
          </ScrollArea>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
